<script>
  import { onDestroy, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, ButtonGroup, Input, Spinner, Tooltip } from '@sveltestrap/sveltestrap';
  import { v4 as uuidv4 } from 'uuid';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';

  import { DomainService } from '@client/services/domain';
  import { GroupsService } from '@client/services/groups';
  import { UsersService } from '@client/services/users';

  import { userStore } from '@client/stores/user';

  import { downloadFile } from '@client/utils/download';
  import { buildUserExportCsv } from '@client/utils/user-export';

  import { USER_REQUEST_PARAMS } from '@client/enums';

  import { deepCopy } from '@common/utils/objects';

  import Builder from './builder/builder.svelte';
  import {
    createEmptyState,
    describeGrouping,
    isStateSubmittable,
    renderExpression,
    resolveBuilderState,
    stripWrapper
  } from './builder/liquid-builder';

  export let group = { exclusions: { googlegroupbindings: [] } };
  // Bumped by the parent form after a successful Update. We use it to reset the
  // preview (the saved query changed) — not to auto-fetch. The membership call is
  // expensive, so it only runs when the admin clicks "Fetch dynamic users".
  export let savedTick = 0;

  // Dynamic-member preview is resolved server-side by SAVED group name, so it
  // cannot run in the create flow (nothing is persisted yet). The parent passes
  // isNew to gate the fetch/preview section; the query editor stays usable.
  export let isNew = false;

  const queryTooltipId = `tooltip-${uuidv4()}`;

  const { addNotification } = getNotificationsContext();
  const notify = (text, error) => {
    addNotification({
      text,
      type: error ? 'error' : 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  let builderState = createEmptyState();

  const initialEditor = resolveBuilderState(group.userAttrs);
  if (initialEditor.state) {
    builderState = initialEditor.state;
  }
  let mode = initialEditor.mode;

  // null = "not loaded yet". [] = "loaded, domain has no user params".
  let availableFields = null;

  // --- Preview state (button-driven) ---
  let items = [];
  let matchCount = null;
  let hasSearched = false;
  let isFetching = false;
  let retrying = false; // true while auto-retrying a timed-out call
  let fetchError = false;

  // True while the "Export Users" transaction (fetch full records -> CSV -> download) runs.
  let exporting = false;

  // Session cache: group name -> sorted matched emails. The endpoint evaluates
  // the SAVED group by name; a successful Update drops the entry.
  let previewCache = {};

  // Monotonic request id so a slow/retried response can't overwrite a newer run.
  let fetchSeq = 0;
  let retryTimer = null;

  // The membership scan can exceed the gateway timeout on large domains, but the
  // backend keeps computing and warms its 1h cache, so a spaced retry usually
  // catches the warmed result. Bounded so we don't retry forever.
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 25000;

  // Show the first N matches with a "Show all" toggle to reveal the rest, plus a
  // search box for large result sets. Filtering runs over the full set.
  const MEMBER_DISPLAY_CAP = 100;
  let memberFilter = '';
  let showAllMembers = false;

  $: memberNeedle = memberFilter.trim().toLowerCase();
  $: filteredMembers = memberNeedle ? items.filter((email) => email.toLowerCase().includes(memberNeedle)) : items;
  $: visibleMembers = showAllMembers ? filteredMembers : filteredMembers.slice(0, MEMBER_DISPLAY_CAP);
  $: showMemberSearch = matchCount !== null && matchCount > MEMBER_DISPLAY_CAP;
  $: canShowAll = filteredMembers.length > MEMBER_DISPLAY_CAP;
  $: canFetch = (group.userAttrs ?? '').trim() !== '';
  // Gate for the "Export Users" button/handler: a non-empty membership must be loaded.
  $: canExport = hasSearched && matchCount > 0 && items.length > 0;

  const resetPreview = () => {
    clearTimeout(retryTimer);
    fetchSeq++; // invalidate any in-flight/pending run
    items = [];
    matchCount = null;
    hasSearched = false;
    isFetching = false;
    retrying = false;
    fetchError = false;
    memberFilter = '';
    showAllMembers = false;
  };

  const runPreview = async (attempt = 0) => {
    clearTimeout(retryTimer);
    const key = group.groupname;
    if (!key || (group.userAttrs ?? '').trim() === '') {
      // The query was cleared (possibly during a retry wait), so stop cleanly —
      // otherwise a retry that lands here would leave the spinner spinning forever.
      isFetching = false;
      retrying = false;
      return;
    }

    // Session-cache hit on a fresh request — show instantly, no network call.
    if (attempt === 0 && Object.prototype.hasOwnProperty.call(previewCache, key)) {
      items = previewCache[key];
      matchCount = items.length;
      hasSearched = true;
      fetchError = false;
      isFetching = false;
      retrying = false;
      memberFilter = '';
      showAllMembers = false;
      return;
    }

    const requestId = ++fetchSeq;
    isFetching = true;
    fetchError = false;
    retrying = attempt > 0;

    let response;
    try {
      response = await GroupsService.getDynamicMembers({ groupname: key });
    } catch (e) {
      response = null;
    }
    if (requestId !== fetchSeq) return; // superseded by a newer run

    if (Array.isArray(response) || response === 'Empty') {
      const matched = Array.isArray(response) ? response.slice().sort() : [];
      items = matched;
      matchCount = matched.length;
      previewCache[key] = matched;
      hasSearched = true;
      isFetching = false;
      retrying = false;
      showAllMembers = false;
      return;
    }

    // Non-array response = failure. The dominant cause is the backend scan
    // exceeding the gateway timeout; the handler keeps running and warms its
    // cache, so wait and retry to catch it. The transport doesn't expose the HTTP
    // status, so any error is treated as retryable up to the cap.
    if (attempt < MAX_RETRIES) {
      retrying = true; // spinner stays visible; the "taking longer" message shows
      retryTimer = setTimeout(() => runPreview(attempt + 1), RETRY_DELAY_MS);
      return;
    }

    isFetching = false;
    retrying = false;
    fetchError = true;
  };

  // Export the currently-fetched dynamic members as the full Users-export CSV.
  // We reuse the users export endpoint with a csvfilter of the matched emails so
  // the CSV columns are byte-identical to the Users page export. Strict parity
  // note: `hideRemoved: null` keeps removed users (the dynamicmembers source
  // includes them); however `_search_users` unconditionally drops Google-suspended
  // and mailbox-not-setup users, so those cannot be included without a backend
  // change (documented narrowing).
  const exportMembers = async () => {
    if (exporting || !canExport) return;
    exporting = true;
    try {
      // Explicit click-time snapshot of the FULL matched set (never the capped/
      // filtered UI projections), taken before the first await so a mid-export
      // refetch/reset can't mutate the in-flight request.
      const data = items.map((email) => ({ username: email }));
      const params = {
        ...deepCopy(USER_REQUEST_PARAMS),
        csvfilter: { data, errors: [] },
        excludeCSV: false,
        hideRemoved: null // literal null — NOT false (false inverts to only-removed)
      };

      const response = await UsersService.exportUsers(params);
      if (typeof response !== 'string') {
        notify($format('label.FAILURE.EXPORT'), true);
        return;
      }

      // Fetch fields fresh (matching the Users page); passed to the util without
      // coercion so a degraded response throws -> caught -> failure toast, rather
      // than a silent username-only CSV.
      const fields = await DomainService.getFields();
      const csv = buildUserExportCsv(response, fields);

      const blob = new Blob([csv], { type: 'text/csv; charset=utf-8' });
      const groupSlug = (group.groupname || 'group').replace(/[^\w.-]+/g, '-');
      const filename = `${$userStore.domain}-${groupSlug}-members-${Date.now()}.csv`;
      downloadFile(blob, filename);
    } catch (e) {
      notify($format('label.FAILURE.EXPORT'), true);
    } finally {
      exporting = false;
    }
  };

  // A successful Update means the saved query changed — drop the cached preview
  // and reset so the admin re-fetches the new membership on demand.
  let lastSavedTick = savedTick;
  $: if (savedTick !== lastSavedTick) {
    lastSavedTick = savedTick;
    if (group.groupname) delete previewCache[group.groupname];
    resetPreview();
  }

  onMount(async () => {
    try {
      const fields = await DomainService.getFields();
      availableFields = Array.isArray(fields) ? fields : [];
    } catch (e) {
      console.warn('Failed to load domain user fields', e);
      availableFields = [];
    }
  });

  onDestroy(() => {
    // Bump fetchSeq so any request still in flight fails its `requestId !== fetchSeq`
    // guard and can't schedule a new retry timer after the component is destroyed.
    fetchSeq++;
    clearTimeout(retryTimer);
  });

  // Reactive sync from builder state -> group.userAttrs. Compare against the
  // stripped (unwrapped) stored value so a legacy `{{ ... }}` expression opened
  // in the builder isn't rewritten to bare form until a real edit — otherwise the
  // parent Save button would enable spuriously on open.
  $: if (mode === 'builder') {
    const next = renderExpression(builderState);
    if (next !== stripWrapper(group.userAttrs ?? '')) {
      group.userAttrs = next;
    }
  }

  $: showIncompleteClauseHint =
    mode === 'builder' &&
    Array.isArray(builderState?.clauses) &&
    builderState.clauses.length > 0 &&
    !isStateSubmittable(builderState) &&
    builderState.clauses.some((c) => !!c?.variable);

  // Human-readable, parenthesized view of how the flat clause list actually
  // groups under LiquidJS (right-to-left, no precedence). Only shown when the
  // grouping is non-obvious — i.e. the operators are mixed, which is exactly
  // when the parentheses (and clause order) change the meaning.
  $: groupingPreview = describeGrouping(builderState);
  $: showGroupingPreview = mode === 'builder' && groupingPreview.includes('(');

  let inner = null;
  const resize = () => {
    if (!inner) return;
    inner.style.height = 'auto';
    inner.style.height = 4 + inner.scrollHeight + 'px';
  };

  const switchToBuilder = () => {
    if (mode === 'builder') return;
    const resolved = resolveBuilderState(group.userAttrs);
    if (resolved.mode === 'builder') {
      if (resolved.state) {
        builderState = resolved.state;
      }
      mode = 'builder';
      return;
    }
    if (!window.confirm($format('label.RAW_TO_BUILDER_WARNING'))) {
      return;
    }
    mode = 'builder';
  };

  const switchToRaw = () => {
    if (mode === 'raw') return;
    const next = renderExpression(builderState);
    if (next !== stripWrapper(group.userAttrs ?? '')) {
      group.userAttrs = next;
    }
    mode = 'raw';
  };
</script>

<div class="query-header">
  <span class="header">
    {$format('label.QUERY')}
    <i id={queryTooltipId} class="os-icon-help-circle query-help" />
    <Tooltip target={queryTooltipId}>{@html $format('label.TOOLTIP.QUERY_BUILDER')}</Tooltip>
  </span>

  <ButtonGroup size="sm">
    <Button active={mode === 'builder'} on:click={switchToBuilder}>
      {$format('label.QUERY_BUILDER_MODE')}
    </Button>
    <Button active={mode === 'raw'} on:click={switchToRaw}>
      {$format('label.QUERY_RAW_MODE')}
    </Button>
  </ButtonGroup>
</div>

{#if mode === 'builder'}
  {#if availableFields === null}
    <!-- pending; render nothing until DomainService.getFields() resolves -->
  {:else if availableFields.length === 0}
    <div class="empty-fields">
      <p>{$format('label.NO_USER_FIELDS_AVAILABLE')}</p>
      <Button color="secondary" size="sm" on:click={() => (mode = 'raw')}>
        {$format('label.QUERY_RAW_MODE')}
      </Button>
    </div>
  {:else}
    <Builder state={builderState} {availableFields} onChange={(next) => (builderState = next)} />
    {#if showIncompleteClauseHint}
      <div class="builder-validation-hint">
        <Icon name="info" />
        <span>{$format('label.INCOMPLETE_CLAUSE_HINT')}</span>
      </div>
    {/if}
    {#if showGroupingPreview}
      <div class="grouping-hint">
        <Icon name="info" />
        <span>{$format('label.EFFECTIVE_GROUPING')} <code>{groupingPreview}</code></span>
      </div>
    {/if}
  {/if}
{:else}
  <Input
    type="textarea"
    rows={1}
    on:input={resize}
    bind:inner
    bind:value={group.userAttrs}
    placeholder={$format('label.TOOLTIP.QUERY_FILTER')}
  />
{/if}

<hr class="preview-divider" />

{#if isNew}
  <p class="preview-blurb">{$format('label.DYNAMIC_PREVIEW_SAVE_TO_ENABLE')}</p>
{:else}
  <div class="preview-panel">
    <p class="preview-blurb">{$format('label.DYNAMIC_PREVIEW_BLURB')}</p>

    {#if isFetching}
      {#if retrying}
        <p class="preview-slow">{$format('label.DYNAMIC_PREVIEW_SLOW')}</p>
      {/if}
      <div class="preview-spinner">
        <Spinner color="primary" />
      </div>
    {:else}
      <Button color="primary" size="sm" disabled={!canFetch} on:click={() => runPreview()}>
        {$format('label.FETCH_DYNAMIC_USERS')}
      </Button>
    {/if}

    {#if fetchError}
      <div class="preview-error">
        <Icon name="alert-triangle" />
        <span>{$format('label.PREVIEW_FETCH_FAILED')}</span>
        <Button color="link" size="sm" on:click={() => runPreview()}>
          {$format('label.RETRY')}
        </Button>
      </div>
    {/if}
  </div>

  {#if hasSearched && !isFetching && !fetchError}
    {#if matchCount === 0}
      <div class="zero-match-warning">
        <Icon name="alert-triangle" />
        <span>{$format('label.ZERO_MATCH_WARNING')}</span>
      </div>
    {:else}
      <div class="header">{$format('label.MATCHING_USERS_COUNT', { values: { users: matchCount } })}</div>

      <div class="export-actions">
        <Button color="secondary" size="sm" disabled={exporting} on:click={exportMembers}>
          {#if exporting}
            <Spinner size="sm" />
          {/if}
          {$format('label.EXPORT_USERS')}
        </Button>
      </div>

      {#if showMemberSearch}
        <Input type="text" bind:value={memberFilter} placeholder={$format('label.SEARCH_USERS')} />
      {/if}

      <div class="members">
        {#each visibleMembers as member}
          <div class="member">{member}</div>
        {/each}
      </div>

      {#if canShowAll}
        <div class="members-actions">
          {#if !showAllMembers}
            <span class="members-note">
              {$format('label.SHOWING_X_OF_Y', {
                values: { shown: visibleMembers.length, total: filteredMembers.length }
              })}
            </span>
          {/if}
          <Button color="link" size="sm" on:click={() => (showAllMembers = !showAllMembers)}>
            {showAllMembers ? $format('label.SHOW_LESS') : $format('label.SHOW_ALL')}
          </Button>
        </div>
      {/if}
    {/if}
  {/if}
{/if}

<style lang="scss" src="./dynamic-members.scss"></style>
